import { createReducer, on } from '@ngrx/store';

import {
  EMPTY_ADVANCED_FEASIBILITY_FILTERS,
  INITIAL_LOCATION_UPLOAD_STATE,
} from '@ps/survey-utils';

import { INITIAL_SURVEY_CREATION_AUDIENCE_STATE } from '../survey-creation-state.constants';
import { SurveyCreationAudienceState } from '../survey-creation-state.model';
import {
  SurveyCreationActions,
  SurveyCreationApiActions,
} from './survey-creation-audience.actions';
import { hasLocaleChanged } from './utils/locale-changes.helper';
import {
  buildMetadataFormStateFromSurvey,
  buildQualificationsFormStateFromSurvey,
  buildQuotasFormStateFromSurvey,
  buildSuppliersMetaDataFromSurvey,
} from './domain/build-audience-form-state-from-survey';

export const surveyCreationAudienceReducer = createReducer(
  INITIAL_SURVEY_CREATION_AUDIENCE_STATE,

  on(
    SurveyCreationActions.clearSurveyId,
    (state): SurveyCreationAudienceState => ({
      ...state,
      surveyId: null,
      error: null,
    }),
  ),

  on(
    SurveyCreationActions.init,
    (state, { surveyId }): SurveyCreationAudienceState => {
      // Idempotence guard: re-initialising the survey already in memory keeps
      // it rather than forcing a re-fetch. Navigations into the edit page now
      // reset first, so in practice this only absorbs a repeated init.
      const alreadyHydrated =
        !!surveyId && state.surveyId === surveyId && !!state.survey;

      return alreadyHydrated
        ? state
        : { ...INITIAL_SURVEY_CREATION_AUDIENCE_STATE, surveyId };
    },
  ),

  // Hydrate the in-memory survey from the API response.
  on(
    SurveyCreationApiActions.loadSurveyForEditSuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      survey,
      advancedFeasibilityFilters: survey.advancedFeasibilityFilters,
      metadata: buildMetadataFormStateFromSurvey(survey),
      qualifications: buildQualificationsFormStateFromSurvey(survey),
      quotas: buildQuotasFormStateFromSurvey(survey),
      suppliersMetaData: buildSuppliersMetaDataFromSurvey(survey),
      error: null,
    }),
  ),

  on(
    SurveyCreationActions.updateMetadata,
    (state, { metadata }): SurveyCreationAudienceState => ({
      ...state,
      metadata,
      advancedFeasibilityFilters: hasLocaleChanged(state.metadata, metadata)
        ? { ...EMPTY_ADVANCED_FEASIBILITY_FILTERS }
        : state.advancedFeasibilityFilters,
    }),
  ),

  on(
    SurveyCreationActions.updateQualifications,
    (state, { qualifications }): SurveyCreationAudienceState => ({
      ...state,
      qualifications,
    }),
  ),

  on(
    SurveyCreationActions.updateQuotas,
    (state, { quotas }): SurveyCreationAudienceState => ({
      ...state,
      quotas,
    }),
  ),

  on(
    SurveyCreationActions.uploadLocationCodes,
    (state, { qualificationCode }): SurveyCreationAudienceState => ({
      ...state,
      locationFileUpload: {
        qualificationCode,
        loading: true,
        error: null,
        filePath: null,
        result: null,
      },
    }),
  ),

  on(
    SurveyCreationApiActions.uploadLocationCodesSuccess,
    (state, { qualificationCode, result }): SurveyCreationAudienceState => ({
      ...state,
      locationFileUpload: {
        qualificationCode,
        loading: false,
        error: null,
        filePath: result.filePath,
        result,
      },
    }),
  ),

  on(
    SurveyCreationApiActions.uploadLocationCodesFailure,
    (state, { qualificationCode, error }): SurveyCreationAudienceState => ({
      ...state,
      locationFileUpload: {
        qualificationCode,
        loading: false,
        error,
        filePath: null,
        result: null,
      },
    }),
  ),

  on(
    SurveyCreationActions.clearLocationCodes,
    (state, { qualificationCode }): SurveyCreationAudienceState =>
      state.locationFileUpload.qualificationCode === qualificationCode
        ? { ...state, locationFileUpload: { ...INITIAL_LOCATION_UPLOAD_STATE } }
        : state,
  ),

  on(
    SurveyCreationActions.applyAdvancedFilters,
    (state, { filters }): SurveyCreationAudienceState => ({
      ...state,
      advancedFeasibilityFilters: filters,
      error: null,
    }),
  ),

  on(
    SurveyCreationApiActions.createStandardSurveySuccess,
    SurveyCreationApiActions.createSurveyFromTemplateSuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      surveyId: survey.id,
      survey,
      error: null,
    }),
  ),

  on(
    SurveyCreationApiActions.createStandardSurveyFailure,
    SurveyCreationApiActions.createSurveyFromTemplateFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      surveyId: state.surveyId ?? state.survey?.id ?? null,
      error,
    }),
  ),

  on(
    SurveyCreationApiActions.saveSurveySuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      surveyId: survey.id,
      survey,
      error: null,
      metadata: { ...state.metadata, isDirty: false },
      qualifications: { ...state.qualifications, isDirty: false },
      quotas: { ...state.quotas, isDirty: false },
    }),
  ),

  on(
    SurveyCreationApiActions.saveSurveyFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  // No cloneSurveySuccess handler: navigateToEditSurvey$ dispatches reset()
  // right after it, so anything written here would be wiped before it is read.

  on(
    SurveyCreationApiActions.cloneSurveyFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationApiActions.loadSurveyForEditFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationApiActions.loadSurveyTagsSuccess,
    (state, { tagIds }): SurveyCreationAudienceState => ({
      ...state,
      error: null,
      tagLinks: {
        ...state.tagLinks,
        linkedTagIds: tagIds,
      },
    }),
  ),

  on(
    SurveyCreationApiActions.loadSurveyTagsFailure,
    (state): SurveyCreationAudienceState => ({
      ...state,
      tagLinks: {
        ...state.tagLinks,
        linkedTagIds: [],
      },
    }),
  ),

  on(
    SurveyCreationApiActions.linkSurveyTagsSuccess,
    (state): SurveyCreationAudienceState => ({
      ...state,
      error: null,
      tagLinks: {
        ...state.tagLinks,
        linkTagsError: null,
      },
    }),
  ),

  on(
    SurveyCreationApiActions.linkSurveyTagsFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      tagLinks: {
        ...state.tagLinks,
        linkTagsError: error,
      },
    }),
  ),

  on(
    SurveyCreationActions.setSuppliersMetaDataBlendId,
    (state, { blendId }): SurveyCreationAudienceState => ({
      ...state,
      suppliersMetaData: { blendId },
    }),
  ),

  on(
    SurveyCreationActions.reset,
    (): SurveyCreationAudienceState => INITIAL_SURVEY_CREATION_AUDIENCE_STATE,
  ),
);
